// Registri generator soal Matematika dan penyusun ronde kuis.
import { shuffle, R } from '../../core/random.js';
import { siapkanSoalBank } from '../bersama.js';
import { gSenilai, gUrut, gRelasi, gDiskon, gCacah, gTambahPecahan, gKaliPecahan, gKPK, gFPB, gFaktor } from './generators/bilangan.js';
import { gKelilingLuas, gLuasBangun, gVolume, gKubusSatuan, gDenah, gDadu, gPanjang, gBerat, gVolSatuan, gWaktu, gKecepatan, gSudut, gTaksir, gSisa } from './generators/geometri-pengukuran.js';
import { gBatang, gPiktogram, gTabel } from './generators/data.js';
import { gSifatBangun, gCukupUang, gBandingSetengah, gKesimpulanData, gTepatWaktu, gMuatWadah } from './generators/kategori.js';

// Kunci di sini dipakai juga oleh `bobotTopik` di config.json.
export const GENERATOR = {
  'pecahan-senilai': gSenilai,
  'urutan-pecahan': gUrut,
  'pecahan-desimal-persen': gRelasi,
  'diskon': gDiskon,
  'bilangan-cacah': gCacah,
  'tambah-kurang-pecahan': gTambahPecahan,
  'kali-bagi-pecahan': gKaliPecahan,
  'kpk': gKPK,
  'fpb': gFPB,
  'faktor-kelipatan': gFaktor,
  'banding-setengah': gBandingSetengah,
  'cukup-uang': gCukupUang,
  'keliling-luas': gKelilingLuas,
  'luas-bangun': gLuasBangun,
  'sifat-bangun-datar': gSifatBangun,
  'volume': gVolume,
  'kubus-satuan': gKubusSatuan,
  'tampak-bangun': gDenah,
  'dadu': gDadu,
  'satuan-panjang': gPanjang,
  'satuan-berat': gBerat,
  'satuan-volume': gVolSatuan,
  'muat-wadah': gMuatWadah,
  'waktu': gWaktu,
  'tepat-waktu': gTepatWaktu,
  'kecepatan': gKecepatan,
  'sudut': gSudut,
  'penaksiran': gTaksir,
  'pembagian-bersisa': gSisa,
  'diagram-batang': gBatang,
  'piktogram': gPiktogram,
  'tabel-frekuensi': gTabel,
  'kesimpulan-data': gKesimpulanData,
};

// Bobot dipakai bila config.json tidak menyebutkan topik tersebut.
export const BOBOT_BAWAAN = Object.fromEntries(Object.keys(GENERATOR).map(k => [k, 1]));
Object.assign(BOBOT_BAWAAN, {
  'bilangan-cacah': 2, 'tambah-kurang-pecahan': 2, 'kali-bagi-pecahan': 2, 'kpk': 2,
  'diagram-batang': 2, 'piktogram': 2, 'tabel-frekuensi': 2,
});

export function buatSoal(id) {
  const gen = GENERATOR[id] || gSisa;
  for (let i = 0; i < 8; i++) {
    try {
      const q = gen();
      if (q) return { ...q, sumber: 'generator', gen: id };
    } catch (e) {
      console.warn('Generator gagal:', id, e);
    }
  }
  return { ...gSisa(), sumber: 'generator', gen: 'pembagian-bersisa' };
}

/**
 * Susun satu ronde berisi `n` soal.
 * @param {number} n jumlah soal
 * @param {{bobot?: Record<string, number>, bank?: object[], porsiBank?: number}} opsi
 *   bobot: bobot per topik (0 = nonaktif); bank: soal dari bank soal yang sudah dinormalisasi;
 *   porsiBank: bagian ronde (0–1) yang diambil dari bank soal.
 */
export function susunRonde(n, { bobot = {}, bank = [], porsiBank = 0 } = {}) {
  const w = { ...BOBOT_BAWAAN, ...bobot };
  const pool = [];
  for (const [id, k] of Object.entries(w)) {
    if (!GENERATOR[id]) continue;
    for (let i = 0; i < Math.max(0, Math.round(k)); i++) pool.push(id);
  }
  if (!pool.length) pool.push(...Object.keys(GENERATOR));

  const nBank = Math.min(bank.length, Math.round(n * Math.min(1, Math.max(0, porsiBank))));
  const nGen = n - nBank;
  let deck = [];
  while (deck.length < nGen) deck.push(...shuffle(pool));
  deck = deck.slice(0, nGen);
  // Hindari topik yang sama muncul berturut-turut.
  for (let i = 1; i < deck.length; i++) {
    if (deck[i] !== deck[i - 1]) continue;
    const j = deck.findIndex((g, k) => k > i && g !== deck[i] && g !== deck[i - 1] && (k + 1 >= deck.length || deck[k + 1] !== deck[i]));
    if (j > 0) [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  const soal = deck.map(buatSoal);
  for (const b of shuffle(bank).slice(0, nBank)) soal.splice(R(0, soal.length), 0, siapkanSoalBank(b));
  return soal;
}
